import React, { useState } from 'react';
import { useClient } from '../../../../api/api-client';
import { useQueryCall, useMutationCall } from '../../../../api/react-client';
import { OBHQ_ENDPOINTS } from './constants';
import OBHQTable from './OBHQTable';
import OBHQModal from './OBHQModal';
import { Clock, Plus } from 'lucide-react';
import Button from '../../../../components/ComponentsV1/Button';
import { Modal } from '../../../../UiKit';
import toast from 'react-hot-toast';
import { Skeleton } from '../../../../components/ComponentsV1/Skeleton/Skeleton';

const TABLE_SKELETON_ROWS = [
  { cols: 1, height: '6rem' },
  { cols: 1, height: '6rem' },
  { cols: 1, height: '6rem' },
];

const OutOfBusinessHoursQueue = () => {
  const client = useClient();

  const [queueData, setQueueData] = useState([]);
  const [formOptions, setFormOptions] = useState(null);

  // modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null); // null = create, item = update

  // delete confirmation state
  const [deleteState, setDeleteState] = useState({ open: false, queueName: '' });
  const [isSaving, setIsSaving] = useState(false);

  // Fetch form options
  const { isLoading: isFormOptionsLoading } = useQueryCall(
    { enabled: true },
    null,
    ['obhq_form_options'],
    () =>
      client(OBHQ_ENDPOINTS.FORM_OPTIONS).then((res) => {
        if (res) setFormOptions(res);
        return res;
      }),
  );

  // Fetch queue list
  const { isLoading: isDataLoading, refetch: refetchList } = useQueryCall(
    { enabled: true },
    null,
    ['obhq_list'],
    () =>
      client(OBHQ_ENDPOINTS.GET).then((res) => {
        if (Array.isArray(res)) setQueueData(res);
        return res;
      }),
  );

  // Delete mutation
  const { mutate: deleteConfig } = useMutationCall(
    {
      onSuccess: (res) => {
        toast.success(res?.message ?? 'Config deleted successfully.');
        handleSaveDone();
      },
      onError: () => {
        toast.error('Failed to delete. Please try again.');
        handleSaveDone();
      },
    },
    OBHQ_ENDPOINTS.DELETE,
    ['obhq_delete'],
    { doNotShowSuccessToast: true },
    ({ queueName }) => {
      if (!queueName) {
        return Promise.reject(new Error('Queue name is required'));
      }
      return client(
        `${OBHQ_ENDPOINTS.DELETE}?queue_name=${encodeURIComponent(queueName)}`,
        { method: 'DELETE' },
      );
    },
  );

  const handleOpenCreate = () => {
    setSelectedItem(null);
    setModalOpen(true);
  };

  const handleOpenUpdate = (item) => {
    setSelectedItem(item);
    setModalOpen(true);
  };

  const handleDelete = (queueName) => {
    if (!queueName || !queueName.trim()) {
      toast.error('Invalid queue name.');
      return;
    }
    setDeleteState({ open: true, queueName });
  };

  const confirmDelete = () => {
    const queueNameToDelete = deleteState?.queueName;
    if (!queueNameToDelete || !queueNameToDelete.trim()) {
      toast.error('Invalid queue name.');
      setDeleteState({ open: false, queueName: '' });
      return;
    }
    setDeleteState({ open: false, queueName: '' });
    setIsSaving(true);
    deleteConfig({ queueName: queueNameToDelete });
  };

  const isLoading = isDataLoading || isFormOptionsLoading || isSaving;

  const handleSaveDone = async () => {
    await refetchList();
    setIsSaving(false);
  };

  return (
    <div className='tw-w-full tw-flex tw-flex-col tw-h-max tw-gap-5 tw-p-[2rem] tw-rounded-[1rem] tw-relative tw-bg-white'>
      <div className='tw-w-full tw-flex tw-justify-between tw-items-center'>
        <div className='tw-text-[2.2rem] tw-font-medium tw-text-zinc-700/95 tw-leading- tw-flex tw-items-center'>
          Out of Business Hours Config
          <Clock className='tw-ml-3 tw-mt-[1px]' size={23} strokeWidth={2.3} />
        </div>
        <Button
          onClick={handleOpenCreate}
          title={
            <div className='tw-flex tw-items-center tw-whitespace-nowrap tw-gap-2'>
              <Plus size={15} />
              Add New
            </div>
          }
        />
      </div>

      <div className='tw-mt-6 tw-flex-col'>
        {isLoading ? (
          <div className='tw-mt-4'>
            <Skeleton rows={TABLE_SKELETON_ROWS} />
          </div>
        ) : (
          <OBHQTable
            queueData={queueData}
            onUpdate={handleOpenUpdate}
            onDelete={handleDelete}
          />
        )}
      </div>

      {/* Create / Update Modal */}
      {modalOpen && (
        <OBHQModal
          key={selectedItem ? selectedItem?.queue_name : 'create'}
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          item={selectedItem}
          formOptions={formOptions}
          refetchData={handleSaveDone}
          onSaveStart={() => { setModalOpen(false); setIsSaving(true); }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteState.open && (
        <Modal
          isVisible={deleteState.open}
          padding={0}
          paddingTop={'1rem'}
          type='large'
          containerStyles={{ margin: '16px 0 20px 0', padding: '0 20px' }}
          titleComponent={
            <span className='tw-text-[1.8rem] tw-font-medium tw-text-zinc-600'>
              Delete Queue
            </span>
          }
          handleClose={() => setDeleteState({ open: false, queueName: '' })}
        >
          <div className='tw-flex tw-flex-col tw-gap-8 tw-py-4'>
            <div className='tw-flex tw-flex-col tw-gap-3'>
              <p className='tw-text-grey-800 tw-text-[17px] tw-font-medium tw-m-0'>
                Are you sure you want to delete "{deleteState.queueName}"?
              </p>
              <p className='tw-text-grey-500 tw-text-[14px] tw-m-0'>
                This action cannot be undone.
              </p>
            </div>
            <div className='tw-flex tw-justify-end tw-gap-3 tw-pb-4'>
              <Button
                type='link'
                title='Cancel'
                onClick={() => setDeleteState({ open: false, queueName: '' })}
                className='tw-font-medium hover:tw-bg-grey-100 tw-duration-200 tw-ease-in-out tw-transition-all'
              />
              <button
                onClick={confirmDelete}
                className='tw-flex tw-items-center tw-gap-2 tw-bg-red-500 tw-text-white tw-border-none tw-rounded-xl tw-px-6 tw-py-3 tw-cursor-pointer tw-font-medium hover:tw-bg-red-600 tw-transition-all tw-duration-200 tw-ease-in-out tw-text-[15px]'
              >
                Delete
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default OutOfBusinessHoursQueue;

